import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Mail, Phone, Shield, Building2, FileText, Upload, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { isValidEmail } from '../lib/validation';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, } from '../components/ui/Card';

type FieldErrors = {
  company_name?: string;
  name?: string;
  email?: string;
  phone_number?: string;
  password?: string;
  password_confirmation?: string;
  otp?: string;
};

export function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'account' | 'otp' | 'business' | 'verification' | 'success'>('account');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
    company_name: '',
    business_type: 'manufacturer',
    registration_number: '',
    year_established: '',
    employee_count: '',
    products_description: '',
    otp: '',
  });

  const [verificationDocs, setVerificationDocs] = useState<Array<{ name: string; file: File; preview: string; type: string }>>([]);
  // Document types already stored server-side this session, so a retry after a
  // partial failure doesn't re-upload (the endpoint appends without dedupe)
  const [uploadedTypes, setUploadedTypes] = useState<string[]>([]);

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const setField = (key: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    clearFieldError(key as keyof FieldErrors);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: FieldErrors = {};
    if (!formData.company_name.trim()) {
      errors.company_name = 'Company name is required.';
    }
    if (!formData.name.trim()) {
      errors.name = 'Contact name is required.';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Enter a valid email address.';
    }
    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required.';
    }
    if (!formData.password) {
      errors.password = 'Password is required.';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }
    if (!formData.password_confirmation) {
      errors.password_confirmation = 'Please confirm your password.';
    } else if (formData.password_confirmation !== formData.password) {
      errors.password_confirmation = 'Passwords do not match.';
    }
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);

    // Add timeout to prevent indefinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false);
      toast.error('Request timed out. Please try again.');
    }, 30000); // 30 second timeout

    try {
      await api.post('/otp/send', { email: formData.email });
      clearTimeout(timeoutId);
      toast.success('OTP sent to your email!');
      setLoading(false);
      setStep('otp');
      setCountdown(300);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      clearTimeout(timeoutId);
      const errorMsg = err.response?.data?.message || '';
      const errorLower = errorMsg.toLowerCase();

      // Map specific error messages
      if (errorLower.includes('email') && (errorLower.includes('already') || errorLower.includes('exist'))) {
        toast.error('This email is already registered. Please log in or use a different email.');
      } else if (err.response?.data?.errors?.email) {
        toast.error(err.response.data.errors.email[0] || 'Invalid email address');
      } else if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).flat().forEach((msg: any) => {
          toast.error(msg);
        });
      } else {
        toast.error('Failed to send OTP');
      }
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.otp.length !== 6) {
      setFieldErrors((prev) => ({ ...prev, otp: 'Please enter the 6-digit code.' }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, otp: undefined }));
    setLoading(true);

    // Add timeout to prevent indefinite loading
    const timeoutId = setTimeout(() => {
      setLoading(false);
      toast.error('Verification request timed out. Please try again.');
    }, 30000); // 30 second timeout

    try {
      const { data } = await api.post('/register/manufacturer', {
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        otp: formData.otp,
      });
      // Persist the session now so the verification upload is authenticated
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      clearTimeout(timeoutId);
      setLoading(false);
      setStep('business');
    } catch (err: any) {
      clearTimeout(timeoutId);
      const errorMsg = err.response?.data?.message || '';
      const errorLower = errorMsg.toLowerCase();

      // Map specific error messages
      if (errorLower.includes('otp') && (errorLower.includes('invalid') || errorLower.includes('expired'))) {
        toast.error('OTP is invalid or has expired. Please request a new OTP.');
      } else if (errorLower.includes('email') && (errorLower.includes('already') || errorLower.includes('exist'))) {
        toast.error('This email is already registered. Please use a different email.');
      } else if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).flat().forEach((msg: any) => {
          toast.error(msg);
        });
      } else {
        toast.error(errorMsg || 'Verification failed');
      }
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      setFieldErrors((prev) => ({ ...prev, company_name: 'Company name is required.' }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, company_name: undefined }));
    setStep('verification');
  };

  const handleDocUpload = (type: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const newDoc = { name: file.name, file, preview: reader.result as string, type };
      setVerificationDocs(prev => [...prev.filter(d => d.type !== type), newDoc]);
    };
    reader.readAsDataURL(file);
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const newlyUploaded: string[] = [];
    try {
      // The API accepts one document per call: document_type + document
      for (const doc of verificationDocs) {
        if (uploadedTypes.includes(doc.type)) continue;
        const docForm = new FormData();
        docForm.append('document_type', doc.type);
        docForm.append('document', doc.file);
        await api.post('/manufacturer/verification', docForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newlyUploaded.push(doc.type);
      }

      setLoading(false);
      setStep('success');
      toast.success('Registration submitted! We will review your documents.');
    } catch (err: any) {
      setUploadedTypes((prev) => {
        const merged = prev.concat(newlyUploaded);
        return merged.filter((t, i) => merged.indexOf(t) === i);
      });
      const errorMsg = err.response?.data?.message || '';
      const errorLower = errorMsg.toLowerCase();

      // Map specific error messages
      if (errorLower.includes('company') && (errorLower.includes('already') || errorLower.includes('exist'))) {
        toast.error('This company is already registered. Please use a different company name.');
      } else if (errorLower.includes('document') || errorLower.includes('verification')) {
        toast.error('Please upload all required verification documents.');
      } else if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).flat().forEach((msg: any) => {
          toast.error(msg);
        });
      } else {
        toast.error(errorMsg || 'Failed to submit verification');
      }
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);

    // Add timeout to prevent indefinite loading
    const timeoutId = setTimeout(() => {
      setResending(false);
      toast.error('Request timed out. Please try again.');
    }, 30000); // 30 second timeout

    try {
      await api.post('/otp/send', { email: formData.email });
      clearTimeout(timeoutId);
      toast.success('New OTP sent!');
      setCountdown(300);
      setResending(false);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      clearTimeout(timeoutId);
      toast.error('Failed to resend OTP');
      setResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accountInputClass = (err?: string) =>
    `pl-12 h-12 bg-gray-50 rounded-2xl transition-all ${
      err ? 'border border-red-400 focus:ring-2 focus:ring-red-400' : 'border-none focus:ring-2 focus:ring-emerald-500'
    }`;

  const fieldErrorText = (err?: string) =>
    err ? <p className="ml-1 text-xs font-semibold text-red-500">{err}</p> : null;

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Branding */}
        <div className="text-center mb-8 animate-in fade-in slide-in-from-top-4 duration-700 flex flex-col items-center">
          <img src="/assets/logo/logo.svg" className="h-12 w-auto object-contain mb-4" alt="ShoplyCircle Logo" />
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Create Manufacturer Account</h1>
          <p className="mt-2 text-sm text-gray-500 font-medium italic">Join Shoplycircle Manufacturing Network</p>
        </div>

        <Card className="bg-white rounded-3xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden animate-in zoom-in-95 duration-500">
          <CardContent className="p-8 sm:p-10">
            {/* Step 1: Account Details */}
            {step === 'account' && (
              <form onSubmit={handleSendOtp} className="space-y-6" noValidate>
                <div className="space-y-5">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Company Name</label>
                    <div className="relative">
                      <Building2 className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.company_name ? 'text-red-400' : 'text-gray-400'}`} />
                      <Input
                        placeholder="Company Name"
                        className={accountInputClass(fieldErrors.company_name)}
                        value={formData.company_name}
                        onChange={e => setField('company_name', e.target.value)}
                        aria-invalid={!!fieldErrors.company_name}
                      />
                    </div>
                    {fieldErrorText(fieldErrors.company_name)}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Contact Name</label>
                    <div className="relative">
                      <User className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.name ? 'text-red-400' : 'text-gray-400'}`} />
                      <Input
                        placeholder="Your Name"
                        className={accountInputClass(fieldErrors.name)}
                        value={formData.name}
                        onChange={e => setField('name', e.target.value)}
                        aria-invalid={!!fieldErrors.name}
                      />
                    </div>
                    {fieldErrorText(fieldErrors.name)}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email Address</label>
                    <div className="relative">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.email ? 'text-red-400' : 'text-gray-400'}`} />
                      <Input
                        type="email"
                        placeholder="contact@example.com"
                        className={accountInputClass(fieldErrors.email)}
                        value={formData.email}
                        onChange={e => setField('email', e.target.value)}
                        aria-invalid={!!fieldErrors.email}
                      />
                    </div>
                    {fieldErrorText(fieldErrors.email)}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Phone Number</label>
                    <div className="relative">
                      <Phone className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.phone_number ? 'text-red-400' : 'text-gray-400'}`} />
                      <Input
                        placeholder="+977 98XXXXXXXX"
                        className={accountInputClass(fieldErrors.phone_number)}
                        value={formData.phone_number}
                        onChange={e => setField('phone_number', e.target.value)}
                        aria-invalid={!!fieldErrors.phone_number}
                      />
                    </div>
                    {fieldErrorText(fieldErrors.phone_number)}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Password</label>
                      <div className="relative">
                        <Shield className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.password ? 'text-red-400' : 'text-gray-400'}`} />
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className={accountInputClass(fieldErrors.password)}
                          value={formData.password}
                          onChange={e => setField('password', e.target.value)}
                          aria-invalid={!!fieldErrors.password}
                        />
                      </div>
                      {fieldErrorText(fieldErrors.password)}
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Confirm Password</label>
                      <div className="relative">
                        <Shield className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.password_confirmation ? 'text-red-400' : 'text-gray-400'}`} />
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className={accountInputClass(fieldErrors.password_confirmation)}
                          value={formData.password_confirmation}
                          onChange={e => setField('password_confirmation', e.target.value)}
                          aria-invalid={!!fieldErrors.password_confirmation}
                        />
                      </div>
                      {fieldErrorText(fieldErrors.password_confirmation)}
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-lg shadow-xl shadow-emerald-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOtp} className="space-y-6" noValidate>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 animate-in zoom-in-50 mb-4">
                    <Shield className="h-8 w-8" />
                  </div>
                  <h2 className="text-2xl font-extrabold text-gray-900">Verify Your Email</h2>
                  <p className="mt-2 text-sm text-gray-500">
                    We sent a 6-digit code to <span className="font-medium text-emerald-600">{formData.email}</span>
                  </p>
                </div>

                <div className="space-y-5">
                  <div className="flex flex-col items-center gap-2">
                    <Input
                      type="text"
                      maxLength={6}
                      placeholder="123456"
                      className={`w-40 text-center text-3xl font-bold tracking-widest bg-gray-50 rounded-2xl transition-all ${
                        fieldErrors.otp ? 'border border-red-400 focus:ring-2 focus:ring-red-400' : 'border-none focus:ring-2 focus:ring-emerald-500'
                      }`}
                      value={formData.otp}
                      onChange={e => setField('otp', e.target.value.replace(/\D/g, ''))}
                      aria-invalid={!!fieldErrors.otp}
                    />
                    {fieldErrorText(fieldErrors.otp)}
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || formData.otp.length !== 6}
                    className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-lg shadow-xl shadow-emerald-600/20 transition-all active:scale-[0.98]"
                  >
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </div>

                <div className="text-center space-y-3">
                  <p className="text-sm text-gray-500">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resending || countdown > 0}
                      className={`font-bold ${countdown > 0 ? 'text-gray-400' : 'text-emerald-600 hover:underline'}`}
                    >
                      {countdown > 0 ? `Resend in ${formatTime(countdown)}` : 'Resend Code'}
                    </button>
                  </p>
                  <button
                    type="button"
                    onClick={() => setStep('account')}
                    className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                  >
                    ← Back to account details
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Business Details */}
            {step === 'business' && (
              <form onSubmit={handleBusinessSubmit} className="space-y-6" noValidate>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-extrabold text-gray-900">Tell Us About Your Business</h2>
                  <p className="mt-2 text-sm text-gray-500">Help us understand your manufacturing operations</p>
                </div>

                <div className="space-y-5">
                  <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700">Company Name</label>
                    <div className="relative">
                      <Building2 className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${fieldErrors.company_name ? 'text-red-400' : 'text-gray-400'}`} />
                      <Input
                        value={formData.company_name}
                        onChange={e => setField('company_name', e.target.value)}
                        className={accountInputClass(fieldErrors.company_name)}
                        aria-invalid={!!fieldErrors.company_name}
                      />
                    </div>
                    {fieldErrorText(fieldErrors.company_name)}
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700">Business Type</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <select
                        value={formData.business_type}
                        onChange={e => setFormData({...formData, business_type: e.target.value})}
                        className="pl-12 h-12 w-full bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500 transition-all appearance-none cursor-pointer"
                      >
                        <option value="manufacturer">Manufacturer</option>
                        <option value="wholesaler">Wholesaler</option>
                        <option value="distributor">Distributor</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700">Registration Number</label>
                    <Input
                      value={formData.registration_number}
                      onChange={e => setFormData({...formData, registration_number: e.target.value})}
                      className="h-12"
                      placeholder="e.g., 123456789"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-bold text-gray-700">Year Established</label>
                      <Input
                        type="number"
                        value={formData.year_established}
                        onChange={e => setFormData({...formData, year_established: e.target.value})}
                        className="h-12"
                        placeholder="2020"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-bold text-gray-700">Employee Count</label>
                      <Input
                        type="number"
                        value={formData.employee_count}
                        onChange={e => setFormData({...formData, employee_count: e.target.value})}
                        className="h-12"
                        placeholder="50"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-bold text-gray-700">Products Description</label>
                    <textarea
                      value={formData.products_description}
                      onChange={e => setFormData({...formData, products_description: e.target.value})}
                      className="w-full h-24 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                      placeholder="Describe the products you manufacture..."
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('account')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button type="submit" className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700">
                    Continue
                  </Button>
                </div>
              </form>
            )}

            {/* Step 4: Verification Documents */}
            {step === 'verification' && (
              <form onSubmit={handleVerificationSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-extrabold text-gray-900">Verification Documents</h2>
                  <p className="mt-2 text-sm text-gray-500">Upload documents to verify your business</p>
                </div>

                <div className="space-y-4">
                  {[
                    { type: 'identity_proof', label: 'Identity Proof (Government ID)', desc: 'Passport, driver license, or national ID' },
                    { type: 'tax_id', label: 'Tax Registration Certificate', desc: 'VAT certificate or tax ID document' },
                    { type: 'business_license', label: 'Business License', desc: 'Manufacturing license or business permit' },
                  ].map(doc => (
                    <div key={doc.type} className="border-2 border-dashed border-gray-200 rounded-2xl p-6 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-1">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <label className="text-sm font-bold text-gray-900 cursor-pointer">{doc.label}</label>
                          <p className="text-xs text-gray-500 mb-2">{doc.desc}</p>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={e => handleDocUpload(doc.type, e)}
                            className="hidden"
                            id={`file-${doc.type}`}
                          />
                          <label htmlFor={`file-${doc.type}`} className="text-emerald-600 text-sm font-medium hover:underline cursor-pointer inline-flex items-center gap-1">
                            <Upload className="h-4 w-4" />
                            {verificationDocs.find(d => d.type === doc.type) ? 'Replace File' : 'Upload File'}
                          </label>
                          {verificationDocs.find(d => d.type === doc.type) && (
                            <span className="text-xs text-emerald-600 ml-2">✓ Uploaded</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('business')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || verificationDocs.length < 3}
                    className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {loading ? 'Submitting...' : 'Submit for Review'}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 5: Success */}
            {step === 'success' && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-100 text-emerald-600 animate-in zoom-in-50 mb-6">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Application Submitted!</h2>
                <p className="text-sm text-gray-500 font-medium mb-8">
                  We will review your documents and approve your account within 1-3 business days.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-14 rounded-2xl bg-gray-900 hover:bg-gray-800 text-white font-black text-lg shadow-xl shadow-gray-900/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  Sign In
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 font-medium mt-8">
          Already have an account?{' '}
          <a href="/login" className="text-emerald-600 font-bold hover:underline">Sign In</a>
        </p>
      </div>
    </div>
  );
}
